import React from "react";
import usePopoverDismiss from "./usePopoverDismiss.js";

function isHtmlElement(value) {
  return typeof HTMLElement !== "undefined" && value instanceof HTMLElement;
}

function isConnectedHtmlElement(value) {
  return isHtmlElement(value) && value.isConnected;
}

export default function useFleetSegmentPopover({
  openSegmentActionKey = "",
  onTogglePopover = null,
}) {
  const anchorRegistryRef = React.useRef(new Map());
  const panelRef = React.useRef(null);
  const [anchorElement, setAnchorElement] = React.useState(null);
  const resolvedOpenSegmentActionKey = typeof openSegmentActionKey === "string"
    ? openSegmentActionKey
    : "";
  const isOpen = Boolean(resolvedOpenSegmentActionKey);
  const resolvedAnchorElement = isOpen
    ? (() => {
        if (isConnectedHtmlElement(anchorElement)) {
          return anchorElement;
        }

        const fallbackAnchorElement = anchorRegistryRef.current.get(resolvedOpenSegmentActionKey);
        return isConnectedHtmlElement(fallbackAnchorElement) ? fallbackAnchorElement : null;
      })()
    : null;

  React.useEffect(() => {
    if (!isOpen) {
      setAnchorElement(null);
      return;
    }

    setAnchorElement((currentAnchorElement) => {
      if (isConnectedHtmlElement(currentAnchorElement)) {
        return currentAnchorElement;
      }

      const fallbackAnchorElement = anchorRegistryRef.current.get(resolvedOpenSegmentActionKey);
      return isConnectedHtmlElement(fallbackAnchorElement) ? fallbackAnchorElement : null;
    });
  }, [isOpen, resolvedOpenSegmentActionKey]);

  const dismissPopover = React.useCallback(() => {
    setAnchorElement(null);
    onTogglePopover?.("");
  }, [onTogglePopover]);

  const togglePopover = React.useCallback((segmentKey, nextAnchorElement = null) => {
    const resolvedSegmentKey = typeof segmentKey === "string" ? segmentKey : "";
    if (!resolvedSegmentKey) {
      dismissPopover();
      return;
    }

    setAnchorElement(isHtmlElement(nextAnchorElement) ? nextAnchorElement : null);
    onTogglePopover?.(resolvedSegmentKey);
  }, [dismissPopover, onTogglePopover]);

  const registerAnchor = React.useCallback((segmentKey, element) => {
    if (!segmentKey) {
      return;
    }

    const registry = anchorRegistryRef.current;
    if (isHtmlElement(element)) {
      registry.set(segmentKey, element);
      if (segmentKey === resolvedOpenSegmentActionKey) {
        setAnchorElement(element);
      }
      return;
    }

    registry.delete(segmentKey);
    if (segmentKey === resolvedOpenSegmentActionKey) {
      setAnchorElement(null);
    }
  }, [resolvedOpenSegmentActionKey]);

  const shouldIgnoreDismissTarget = React.useCallback((target) => {
    if (!(target instanceof Element)) {
      return false;
    }

    return Boolean(
      target.closest('[data-fleet-segment-action-popover="true"]')
      || target.closest('[data-react-confirmation-modal="true"]')
      || target.closest('[data-react-reassignment-popover="true"]')
      || target.closest("[data-timeline-segment-action-key]"),
    );
  }, []);

  usePopoverDismiss({
    anchorElement: resolvedAnchorElement,
    floatingRef: panelRef,
    isOpen,
    onClose: dismissPopover,
    shouldIgnoreTarget: shouldIgnoreDismissTarget,
  });

  const popoverRefs = React.useMemo(() => ({
    anchorElement: resolvedAnchorElement,
    panelRef,
    registerAnchor,
  }), [registerAnchor, resolvedAnchorElement]);

  return {
    dismissPopover,
    isOpen,
    popoverRefs,
    togglePopover,
  };
}